import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { X, Sparkles, Info, CheckCircle } from 'lucide-react';
import { IngredientInfo } from '../data/ingredients';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.xl};
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.lg};
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.glow.mint};
    color: ${theme.colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const CategoryBadge = styled.span<{ $category: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  
  ${props => {
    switch (props.$category) {
      case 'active':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[700]};
        `;
      case 'hydrator':
        return `
          background: ${theme.colors.accent.sky}30;
          color: ${theme.colors.text.primary};
        `;
      case 'exfoliant':
        return `
          background: ${theme.colors.accent.peach}30;
          color: ${theme.colors.text.primary};
        `;
      case 'soothing':
        return `
          background: ${theme.colors.mint[100]};
          color: ${theme.colors.mint[700]};
        `;
      case 'antioxidant':
        return `
          background: ${theme.colors.accent.lavender}30;
          color: ${theme.colors.text.primary};
        `;
      case 'barrier-support':
        return `
          background: ${theme.colors.glow.mint};
          color: ${theme.colors.mint[700]};
        `;
      default:
        return `
          background: ${theme.colors.neutral[100]};
          color: ${theme.colors.neutral[700]};
        `;
    }
  }}
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.primary[500]};
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.colors.mint[500]};
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const ScienceBox = styled.div`
  background: ${theme.colors.background.secondary};
  padding: 1rem;
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.primary[400]};
`;

const ScienceText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const InfoBox = styled.div`
  background: ${theme.colors.glow.mint};
  padding: 1rem;
  border-radius: ${theme.borderRadius.lg};
  margin-top: 1rem;
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
  
  strong {
    color: ${theme.colors.text.primary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const BestForTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

interface IngredientModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredient: IngredientInfo | null;
}

export const IngredientModal: React.FC<IngredientModalProps> = ({ isOpen, onClose, ingredient }) => {
  if (!ingredient) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Sparkles size={24} />
            {ingredient.name}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <CategoryBadge $category={ingredient.category}>
            {ingredient.category.replace('-', ' ')}
          </CategoryBadge>

          <Section>
            <SectionTitle>
              <CheckCircle />
              Key Benefits
            </SectionTitle>
            <BenefitsList>
              {ingredient.benefits.map((benefit, index) => (
                <BenefitItem key={index}>
                  <CheckCircle />
                  {benefit}
                </BenefitItem>
              ))}
            </BenefitsList>
          </Section>

          <Section>
            <SectionTitle>
              <Info />
              How It Works
            </SectionTitle>
            <ScienceBox>
              <ScienceText>{ingredient.scientificExplanation}</ScienceText>
            </ScienceBox>
          </Section>

          <Section>
            <SectionTitle>
              Best For
            </SectionTitle>
            <BestForTags>
              {ingredient.bestFor.map((item, index) => (
                <Tag key={index}>{item}</Tag>
              ))}
            </BestForTags>
          </Section>

          <Section>
            <InfoBox>
              <InfoText>
                <strong>Concentration:</strong> {ingredient.concentration}
              </InfoText>
              {ingredient.notes && (
                <InfoText style={{ marginTop: '0.5rem' }}>
                  <strong>Note:</strong> {ingredient.notes}
                </InfoText>
              )}
            </InfoBox>
          </Section>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};



